import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Crown, Network, Plus, Trash2, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { ConfirmDialog, Dialog } from '../../../components/ui/Dialog';
import { EmptyState } from '../../../components/ui/EmptyState';
import { SelectField, TextField } from '../../../components/ui/Field';
import { ErrorState, PageContainer, PageHeader, TableSkeleton } from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { useCan } from '../../auth/AuthProvider';
import { teamApi, teamKeys, type Department } from '../api';

function NewDepartmentDialog({ open, onOpenChange }: { open: boolean; onOpenChange(o: boolean): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const branches = useQuery({
    queryKey: teamKeys.branches,
    queryFn: () => teamApi.branches({ status: 'active' }),
    enabled: open,
  });
  // Fresh state on every open: the page remounts this dialog with a new key.
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'team' | 'department'>('team');
  const [branchId, setBranchId] = useState('');
  const create = useMutation({
    mutationFn: () => teamApi.createDepartment({ name, kind, branchId: branchId || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: teamKeys.departments });
      toast.success(`${kind === 'team' ? 'Team' : 'Department'} created`);
      onOpenChange(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New team or department"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button loading={create.isPending} disabled={!name.trim()} onClick={() => create.mutate()}>
            Create
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {create.error ? <ErrorState error={create.error} /> : null}
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        <SelectField
          label="Type"
          value={kind}
          onChange={(e) => setKind(e.target.value as 'team' | 'department')}
          options={[
            { value: 'team', label: 'Team' },
            { value: 'department', label: 'Department' },
          ]}
        />
        <SelectField
          label="Branch"
          placeholder="All branches"
          value={branchId}
          onChange={(e) => setBranchId(e.target.value)}
          options={(branches.data?.data ?? []).map((b) => ({ value: b.id, label: b.name }))}
        />
      </div>
    </Dialog>
  );
}

function MembersDialog({
  department,
  onOpenChange,
}: {
  department: Department | null;
  onOpenChange(o: boolean): void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const staff = useQuery({
    queryKey: teamKeys.staff({ pageSize: 100, status: 'active' }),
    queryFn: () => teamApi.staff({ pageSize: 100, status: 'active' }),
    enabled: !!department,
  });
  const [members, setMembers] = useState<{ userId: string; role: 'member' | 'leader' }[]>(() =>
    (department?.members ?? []).filter((m) => m.id).map((m) => ({ userId: m.id!, role: m.role })),
  );
  const save = useMutation({
    mutationFn: () => teamApi.setMembers(department!.id, members),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: teamKeys.departments });
      toast.success('Members updated');
      onOpenChange(false);
    },
  });
  const selected = new Map(members.map((m) => [m.userId, m.role]));
  return (
    <Dialog
      open={!!department}
      onOpenChange={onOpenChange}
      title={`Members of ${department?.name ?? ''}`}
      description="Tick the people in this team and choose one leader."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save members
          </Button>
        </>
      }
    >
      {save.error ? <ErrorState error={save.error} /> : null}
      {staff.isPending ? (
        <TableSkeleton rows={4} />
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line">
          {(staff.data?.data ?? []).map((s) => {
            const role = selected.get(s.id);
            return (
              <li key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  aria-label={`Include ${s.fullName}`}
                  className="size-4 accent-brand-600"
                  checked={!!role}
                  onChange={(e) =>
                    setMembers((all) =>
                      e.target.checked
                        ? [...all, { userId: s.id, role: 'member' }]
                        : all.filter((m) => m.userId !== s.id),
                    )
                  }
                />
                <span className="flex-1">
                  <span className="font-medium text-fg">{s.fullName}</span>
                  <span className="ml-2 text-fg-subtle">{s.primaryBranch?.name ?? ''}</span>
                </span>
                <label className="flex items-center gap-1.5 text-fg-muted">
                  <input
                    type="radio"
                    name="leader"
                    className="accent-brand-600"
                    checked={role === 'leader'}
                    disabled={!role}
                    onChange={() =>
                      setMembers((all) =>
                        all.map((m) => ({ ...m, role: m.userId === s.id ? 'leader' : 'member' })),
                      )
                    }
                  />
                  Leader
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </Dialog>
  );
}

export function DepartmentsPage() {
  const can = useCan();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editingMembers, setEditingMembers] = useState<Department | null>(null);
  const departments = useQuery({ queryKey: teamKeys.departments, queryFn: teamApi.departments });

  return (
    <PageContainer>
      <PageHeader
        title="Teams"
        description="Group staff into teams. Team leaders see and manage their team's work."
        actions={
          can('departments.create') ? (
            <Button leadingIcon={<Plus aria-hidden className="size-4" />} onClick={() => setCreating(true)}>
              New team
            </Button>
          ) : null
        }
      />
      {departments.isPending ? (
        <TableSkeleton />
      ) : departments.isError ? (
        <ErrorState error={departments.error} onRetry={() => void departments.refetch()} />
      ) : departments.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Network className="size-6" />}
            title="No teams yet"
            description="Teams let leaders manage a group's leads and workload."
          />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {departments.data.map((d) => (
            <Card key={d.id}>
              <CardHeader
                title={d.name}
                description={
                  <span className="flex items-center gap-2">
                    <Badge tone="info">{d.kind === 'team' ? 'Team' : 'Department'}</Badge>
                    {d.branch?.name ?? 'All branches'}
                  </span>
                }
                actions={
                  <>
                    {can('departments.edit') ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        leadingIcon={<UserPlus aria-hidden className="size-4" />}
                        onClick={() => setEditingMembers(d)}
                      >
                        Members
                      </Button>
                    ) : null}
                    {can('departments.delete') ? (
                      <ConfirmDialog
                        title={`Delete ${d.name}?`}
                        description="Members stay in the account; only the grouping is removed."
                        confirmLabel={`Delete ${d.name}`}
                        onConfirm={async () => {
                          try {
                            await teamApi.deleteDepartment(d.id);
                            await queryClient.invalidateQueries({ queryKey: teamKeys.departments });
                            toast.success('Deleted');
                          } catch (err) {
                            toast.error(err);
                          }
                        }}
                        trigger={(open) => (
                          <Button size="sm" variant="ghost" aria-label={`Delete ${d.name}`} onClick={open}>
                            <Trash2 aria-hidden className="size-4" />
                          </Button>
                        )}
                      />
                    ) : null}
                  </>
                }
              />
              <CardBody>
                {d.members.length === 0 ? (
                  <p className="text-sm text-fg-subtle">No members yet.</p>
                ) : (
                  <ul className="flex flex-wrap gap-2">
                    {d.members.map((m) => (
                      <li
                        key={m.id}
                        className="flex items-center gap-1 rounded-full bg-surface-sunken px-2.5 py-1 text-xs text-fg"
                      >
                        {m.role === 'leader' ? (
                          <Crown aria-label="Leader" className="size-3.5 text-warning" />
                        ) : null}
                        {m.fullName}
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}
      <NewDepartmentDialog
        key={creating ? 'new-open' : 'new-closed'}
        open={creating}
        onOpenChange={setCreating}
      />
      <MembersDialog
        key={editingMembers?.id ?? 'none'}
        department={editingMembers}
        onOpenChange={(o) => !o && setEditingMembers(null)}
      />
    </PageContainer>
  );
}
